import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { endPoint } from "../../global";
import { useAuth } from "../../context/AuthProvider";
import Loading from "../../components/Loading";
import ResponseAlert from "../../components/ResponseAlert";
import TermDialog from "../../components/TermDialog";

const poppins = { fontFamily: "'Poppins', sans-serif" };

const whiteCard = {
  backgroundColor: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.95)",
  borderRadius: "16px",
};

const Row = ({ label, value, highlight }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="center"
    py={0.8}
  >
    <Typography
      sx={{
        ...poppins,
        fontSize: "0.85rem",
        color: "rgba(15,31,69,0.55)",
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        ...poppins,
        fontSize: "0.85rem",
        fontWeight: highlight ? 700 : 500,
        color: highlight ? "#1e3a6e" : "#0f1f45",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export default function Confirmation() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirm, setConfirm] = useState([]);
  const [dataTransaction, setDataTranscation] = useState("");
  const [loading, setLoading] = useState(false);

  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(true);
  const [openAlert, setOpenAlert] = useState(false);

  const fetchData = async () => {
    setLoading(true);

    try {
      const res = await axios.get(endPoint + "confirmation", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setConfirm(res.data.groupedData || []);
      setDataTranscation(res.data.salesId || "");

      if (res.data.msg) {
        setOpenAlert(true);
        setResponse(res.data.msg);
        setSuccess(true);
      }
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg || "Terjadi kesalahan.");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrderClick = async () => {
    if (!dataTransaction || loading || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLoading(true);

    try {
      const res = await axios.post(
        endPoint + "confirmation",
        {
          data: dataTransaction,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setOpenAlert(true);
      setResponse(res.data.msg || "Konfirmasi berhasil. Kupon sedang dibuat.");
      setSuccess(true);

      setTimeout(() => {
        navigate("/contract", {
          state: {
            salesId: dataTransaction,
            couponStatus: res.data.couponStatus || "processing",
            transactionNo: res.data.transactionNo || "",
          },
        });
      }, 800);
    } catch (error) {
      setOpenAlert(true);
      setResponse(error.response?.data?.msg || "Gagal konfirmasi.");
      setSuccess(false);

      isSubmittingRef.current = false;
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  const totalPaket =
    confirm?.reduce((acc, item) => acc + Number(item.quantity || 0), 0) || 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 480,
        mx: "auto",
        px: 2,
        py: 3,
        zIndex: 1,
        position: "relative",
      }}
    >
      <img
        src="SolarTuffNatGath.png"
        alt="Strength of Loyalty & Relationships"
        style={{ width: 80, objectFit: "contain" }}
      />

      <Typography
        sx={{
          ...poppins,
          fontWeight: 700,
          fontSize: "1.5rem",
          color: "#fff",
          mb: 0.5,
          textAlign: "center",
        }}
      >
        Review Pesanan
      </Typography>

      <Typography
        sx={{
          ...poppins,
          fontWeight: 400,
          fontSize: "0.82rem",
          color: "rgba(255,255,255,0.75)",
          mb: 3,
          textAlign: "center",
        }}
      >
        Periksa kembali detail pesanan Anda
      </Typography>

      {confirm?.map((item, index) => (
        <Box key={index} sx={{ ...whiteCard, p: 2.5, mb: 2 }}>
          <Typography
            sx={{
              ...poppins,
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#0f1f45",
              mb: 1,
            }}
          >
            {item.packageName}
          </Typography>

          {item.products?.map((product, productIndex) => (
            <Typography
              key={productIndex}
              sx={{
                ...poppins,
                fontSize: "0.78rem",
                color: "rgba(15,31,69,0.65)",
                mb: 0.5,
              }}
            >
              • {product.productName}
            </Typography>
          ))}

          <Divider sx={{ borderColor: "rgba(15,31,69,0.1)", my: 1 }} />

          <Box display="flex" justifyContent="space-between">
            <Typography
              sx={{
                ...poppins,
                fontSize: "0.82rem",
                color: "rgba(15,31,69,0.55)",
              }}
            >
              Jumlah
            </Typography>

            <Typography
              sx={{
                ...poppins,
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "#0f1f45",
              }}
            >
              {item.quantity} paket
            </Typography>
          </Box>
        </Box>
      ))}

      <Box sx={{ ...whiteCard, p: 2.5, mb: 3 }}>
        <Typography
          sx={{
            ...poppins,
            fontWeight: 700,
            fontSize: "0.9rem",
            color: "#0f1f45",
            mb: 1.5,
          }}
        >
          Ringkasan Pesanan
        </Typography>

        <Row label="Total Paket" value={`${totalPaket} paket`} />

        <Divider sx={{ borderColor: "rgba(15,31,69,0.1)", my: 1 }} />

        <Row label="Total" value={`${totalPaket} paket`} highlight />
      </Box>

      <Typography
        onClick={() => setIsDialogOpen(true)}
        sx={{
          ...poppins,
          fontSize: "0.8rem",
          color: "#fff",
          textDecoration: "underline",
          cursor: "pointer",
          mb: 2.5,
          textAlign: "center",
        }}
      >
        Syarat dan ketentuan
      </Typography>

      <Button
        variant="contained"
        fullWidth
        onClick={handleOrderClick}
        disabled={!dataTransaction || loading || isSubmitting}
        sx={{
          ...poppins,
          background: "#1e3a6e",
          color: "#fff",
          borderRadius: "14px",
          py: 1.8,
          mb: 1.5,
          fontSize: "1rem",
          fontWeight: 700,
          textTransform: "none",
          boxShadow: "0 6px 24px rgba(15,31,69,0.4)",
          "&:hover": {
            background: "#162d57",
          },
          "&.Mui-disabled": {
            background: "rgba(255,255,255,0.4)",
            color: "rgba(15,31,69,0.3)",
          },
        }}
      >
        {loading ? "Memproses..." : "Lanjutkan ke Konfirmasi"}
      </Button>

      <Button
        variant="outlined"
        fullWidth
        disabled={loading}
        onClick={() => navigate("/offer")}
        sx={{
          ...poppins,
          background: "#fff",
          color: "#1e3a6e",
          border: "2px solid #1e3a6e",
          borderRadius: "14px",
          py: 1.75,
          fontSize: "1rem",
          fontWeight: 600,
          textTransform: "none",
          "&:hover": {
            background: "#f0f4ff",
            border: "2px solid #1e3a6e",
          },
        }}
      >
        Kembali
      </Button>

      <TermDialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />

      <Loading open={loading} />

      <ResponseAlert
        response={response}
        success={success}
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
      />
    </Box>
  );
}
